import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TabProps, RechartsClickData } from './types'
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction, OTIFDrilldownModal, OTDDrilldownModal } from '../drilldown'
import { useQualityMeasureLabel, useQualityMeasureDivisor } from '../../lib/qualityLabels'
import { groupDetailDrillFilters } from './dashboardFilterUtils'

export function GroupDetailKPITab({ data, currency: _currency, filters }: TabProps) {
  const { formatCurrency: _formatCurrency, formatNumber } = useFormatters()
  // OTIF/KPI percentage decimals from Admin Setup -> Regional -> Decimal
  // Precision. This tab shows its percentages in a table and a chart
  // tooltip rather than rings, but they are the same KPI figures and would
  // otherwise disagree with every other tab.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const { openDrilldown } = useDrilldownAction()
  const qualityLabel = useQualityMeasureLabel()
  const qualityDivisor = useQualityMeasureDivisor()

  // Delivery metric mode: conditionally show OTIF and/or OTD sections
  const deliveryMode = data.delivery_metric_mode ?? 'both'
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only'
  const showOTD = deliveryMode === 'both' || deliveryMode === 'otd_only'

  // OTIF/OTD Modal state
  const [isOTIFModalOpen, setIsOTIFModalOpen] = useState(false)
  const [otifModalContext, setOtifModalContext] = useState<string | undefined>(undefined)
  const [isOTDModalOpen, setIsOTDModalOpen] = useState(false)
  const [otdModalContext, setOtdModalContext] = useState<string | undefined>(undefined)

  // Drill filters in the drill API's key names (R-158), with the W3-T5
  // capped window and arrears term the charts above use. A clicked site
  // bar narrows the OTIF drill to that site (see handleChartClick).
  const drilldownFilters = useMemo(() => groupDetailDrillFilters(filters), [filters])
  // The site the OTIF drill is scoped to — set by the bar AND the table row,
  // derived at render so a panel change (mode, dates) is never stale.
  const [clickedCustomerId, setClickedCustomerId] = useState<number | null>(null)
  const otifModalFilters = useMemo(
    () => groupDetailDrillFilters(filters, clickedCustomerId), [filters, clickedCustomerId])

  // Handle chart click for drill-down
  const handleChartClick = (chartType: string, clickData?: RechartsClickData) => {
    // Recharts 3 reports a chart click as activeLabel / activeIndex — the
    // activePayload the older handler read is no longer sent, which is why
    // every click here used to fall through to "All Suppliers". The label is
    // the site name (XAxis dataKey), resolved back to its row for the id.
    const payload = clickData?.activePayload?.[0]?.payload
    const supplier = (payload?.['supplier'] as string | undefined) ?? clickData?.activeLabel
    const clickedCustomerId = (payload?.['customerId'] as number | undefined)
      ?? supplierOtifData.find((row) => row.supplier === supplier)?.customerId

    switch (chartType) {
      case 'otif':
        setClickedCustomerId(clickedCustomerId ?? null)
        setOtifModalContext(supplier || 'All Suppliers')
        setIsOTIFModalOpen(true)
        break
      case 'otd':
        setOtdModalContext(supplier || 'All Suppliers')
        setIsOTDModalOpen(true)
        break
      case 'ppm':
        openDrilldown('ppm', drilldownFilters, supplier || 'All Suppliers')
        break
      default:
        openDrilldown('spend', drilldownFilters)
    }
  }

  // CR-4211: Use real data from API instead of Math.random()
  const otifTarget = data.targets?.otif ?? data.quality?.otifTarget ?? 95 // Q46: field default
  const ppmTarget = data.targets?.ppm ?? data.quality?.ppmTarget ?? 1500
  const otdTarget = data.targets?.otd ?? data.delivery?.otdTarget ?? 90

  // CR-6543: join the three backend arrays by NAME, not array index — the
  // backend does not guarantee ordering, so an index zip could put supplier
  // A's OTIF beside supplier B's OTD. Counts come from the backend payload
  // (total_lines / otif_lines / arrears_lines) instead of being
  // back-derived from the rounded percentage.
  const supplierOtifData = data.siteKPI?.otifBySite?.map((site) => {
    const deliveryEntry = data.siteKPI?.deliveryBySite?.find(
      (d) => d.site === site.site,
    )
    const linesDelivered = site.total_lines
      ?? (deliveryEntry
        ? (deliveryEntry.total ?? deliveryEntry.onTime + deliveryEntry.early + deliveryEntry.late)
        : site.orders || 0)
    // METRIC-DEFINITIONS M3 (wave 3a): total_lines is the CLOSED judged count
    // (C), disjoint from arrears — failed is simply C minus passed.
    const linesFailed = site.total_lines !== undefined && site.otif_lines !== undefined
      ? Math.max(0, site.total_lines - site.otif_lines)
      : 0
    return {
      supplier: site.site,
      customerId: site.customer_id,
      otif: site.otif,
      target: site.target || otifTarget,
      linesDelivered,
      linesFailed,
    }
  }) || []

  const supplierOtdData = data.siteKPI?.deliveryBySite?.map((site) => ({
    supplier: site.site,
    onTime: site.onTime,
    early: site.early,
    late: site.late,
    // CR-6543: backend total (every GRN — the ring's denominator); the
    // bucket sum remains only as a legacy-payload fallback.
    total: site.total ?? site.onTime + site.early + site.late,
  })) || []

  const supplierPpmData = data.siteKPI?.ppmBySite?.map((site) => ({
    supplier: site.site,
    ppm: site.ppm,
    target: site.target || ppmTarget,
    // Reverse the divisor the backend used to compute `ppm`. Using a
    // hardcoded 1,000,000 here when the platform is configured for PPK
    // would inflate the rejected count by 1,000×. See
    // backend/core/quality.py::get_quality_divisor.
    rejected: site.parts > 0 ? Math.round(site.ppm * site.parts / qualityDivisor) : 0,
    delivered: site.parts,
  })) || []

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          Group Detail KPI provides supplier-level breakdown of performance metrics.
          Click on any supplier to drill down into their specific performance data.
        </p>
      </div>

      {/* Supplier OTIF Breakdown - CR-3835: Fixed site name truncation */}
      {showOTIF && (
      <ChartWrapper
        title="OTIF by Supplier"
        data={supplierOtifData}
        exportFilename="otif-by-supplier"
        legend={[
          { color: '#10b981', label: 'OTIF %' },
          { color: '#ef4444', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierOtifData}
            onClick={(e) => handleChartClick('otif', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                name === 'otif' ? `${value.toFixed(kpiDecimals)}%` : value,
                name === 'otif' ? 'OTIF' : name
              ]}
            />
            <Bar dataKey="otif" fill="#10b981" name="OTIF" />
            <ReferenceLine y={otifTarget} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Target ${otifTarget}%`, position: 'right' }} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Supplier OTD Breakdown - CR-3835: Fixed site name truncation */}
      {showOTD && (
      <ChartWrapper
        title="Delivery Performance by Supplier"
        data={supplierOtdData}
        exportFilename="delivery-by-supplier"
        legend={[
          { color: '#10b981', label: 'On Time' },
          { color: '#fbbf24', label: 'Early' },
          { color: '#ef4444', label: 'Late' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierOtdData}
            onClick={(e) => handleChartClick('otd', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar dataKey="onTime" stackId="delivery" fill="#10b981" name="On Time" />
            <Bar dataKey="early" stackId="delivery" fill="#fbbf24" name="Early" />
            <Bar dataKey="late" stackId="delivery" fill="#ef4444" name="Late" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
      )}

      {/* Supplier PPM Breakdown - CR-3835: Fixed site name truncation */}
      <ChartWrapper
        title={`${qualityLabel} by Supplier`}
        data={supplierPpmData}
        exportFilename="ppm-by-supplier"
        legend={[
          { color: '#ef4444', label: qualityLabel },
          { color: '#10b981', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={supplierPpmData}
            onClick={(e) => handleChartClick('ppm', e)}
            style={{ cursor: 'pointer' }}
            margin={{ bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="supplier"
              tick={{ fontSize: 9 }}
              angle={-45}
              textAnchor="end"
              height={120}
              interval={0}
            />
            <YAxis />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) => [
                formatNumber(value),
                name === 'ppm' ? qualityLabel : name
              ]}
            />
            <Bar dataKey="ppm" fill="#ef4444" name={qualityLabel} />
            <ReferenceLine y={ppmTarget} stroke="#10b981" strokeDasharray="5 5" label={{ value: `Target ${ppmTarget}`, position: 'right' }} />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Supplier Summary Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Supplier Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Supplier</th>
                  {showOTIF && <th className="text-right py-2 px-3 font-medium">OTIF %</th>}
                  {showOTD && <th className="text-right py-2 px-3 font-medium">OTD %</th>}
                  <th className="text-right py-2 px-3 font-medium">{qualityLabel}</th>
                  <th className="text-right py-2 px-3 font-medium">Lines Delivered</th>
                  <th className="text-right py-2 px-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {supplierOtifData.slice(0, 10).map((supplier, idx) => {
                  // CR-6543: match rows by supplier name — index zipping
                  // could pair one supplier's OTIF with another's OTD/PPM.
                  const otdEntry = supplierOtdData.find((d) => d.supplier === supplier.supplier)
                  const ppmEntry = supplierPpmData.find((p) => p.supplier === supplier.supplier)
                  // OTD = passing / all GRNs, mirroring the headline ring
                  // (early counts against, denominator includes unstatused).
                  const otd = otdEntry && otdEntry.total > 0 ? (otdEntry.onTime / otdEntry.total * 100) : 0
                  // null OTIF = N/A (nothing to score) — neither good nor bad
                  // null OTIF = N/A (nothing scored yet) — not on target, not off it; it never earns the green row
                  const otifOk = !showOTIF || (supplier.otif !== null && supplier.otif >= otifTarget)
                  const otdOk = !showOTD || otd >= otdTarget
                  const isGood = otifOk && otdOk && (ppmEntry?.ppm || 0) <= ppmTarget

                  return (
                    <tr
                      key={idx}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => {
                        setClickedCustomerId(supplier.customerId ?? null)
                        setOtifModalContext(supplier.supplier)
                        setIsOTIFModalOpen(true)
                      }}
                    >
                      <td className="py-2 px-3">{supplier.supplier}</td>
                      {showOTIF && (
                      <td className={`text-right py-2 px-3 ${supplier.otif === null ? 'text-muted-foreground' : supplier.otif >= otifTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {supplier.otif === null ? 'N/A' : `${supplier.otif.toFixed(kpiDecimals)}%`}
                      </td>
                      )}
                      {showOTD && (
                      <td className={`text-right py-2 px-3 ${otd >= otdTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {otd.toFixed(kpiDecimals)}%
                      </td>
                      )}
                      <td className={`text-right py-2 px-3 ${(ppmEntry?.ppm || 0) <= ppmTarget ? 'text-green-600' : 'text-red-600'}`}>
                        {formatNumber(ppmEntry?.ppm || 0)}
                      </td>
                      <td className="text-right py-2 px-3">{formatNumber(supplier.linesDelivered)}</td>
                      <td className="text-right py-2 px-3">
                        <span className={`px-2 py-1 rounded text-xs ${isGood ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {isGood ? 'Good' : 'Attention'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* OTIF Specialized Drill-Down Modal */}
      <OTIFDrilldownModal
        isOpen={isOTIFModalOpen}
        onClose={() => setIsOTIFModalOpen(false)}
        filters={otifModalFilters}
        contextLabel={otifModalContext}
      />

      {/* OTD Specialized Drill-Down Modal */}
      <OTDDrilldownModal
        isOpen={isOTDModalOpen}
        onClose={() => setIsOTDModalOpen(false)}
        filters={drilldownFilters}
        contextLabel={otdModalContext}
      />
    </div>
  )
}
